import { useEffect, useState } from 'react'
import { useUpdateUserGroups } from '../../../hooks/admin/useUsers'
import type { GroupAssignment } from '../../../types/admin'

interface GroupsTabProps {
  userId: number
  /** Every group, each carrying its own `assigned` flag — not just this user's. */
  groups: GroupAssignment[]
  onDirtyChange: (dirty: boolean) => void
}

/**
 * Group access for one user, saved on its own.
 *
 * Group access used to ride along with the profile form's single Save button.
 * Now that the two live on different tabs that would mean a save on one tab
 * silently committing edits made on another, so this owns its own state and
 * its own save against PUT /users/:id/groups.
 */
export function GroupsTab({ userId, groups, onDirtyChange }: GroupsTabProps) {
  const updateGroups = useUpdateUserGroups()
  const [assignments, setAssignments] = useState<GroupAssignment[]>([])
  const [touched, setTouched] = useState(false)

  // Guarded by `touched`: react-query hands back a fresh `groups` array on
  // every refetch (refetchOnWindowFocus is on), and re-running this repainted
  // every checkbox from the server -- silently discarding edits the admin had
  // not saved yet. Once a checkbox is touched, local state is the source of
  // truth until they save or reset.
  useEffect(() => {
    if (touched) return
    setAssignments(groups.map((group) => ({ ...group })))
  }, [groups, touched])

  const assignedCount = assignments.filter((g) => g.assigned).length
  const allAssigned = assignments.length > 0 && assignedCount === assignments.length

  const hasChanges =
    assignments.length > 0 &&
    assignments.some((g) => {
      const original = groups.find((og) => og.locationId === g.locationId)?.assigned ?? false
      return g.assigned !== original
    })

  useEffect(() => {
    onDirtyChange(hasChanges)
  }, [hasChanges, onDirtyChange])

  const toggle = (locationId: number) => {
    setTouched(true)
    setAssignments((prev) => prev.map((g) => (g.locationId === locationId ? { ...g, assigned: !g.assigned } : g)))
  }

  const setAll = (assigned: boolean) => {
    setTouched(true)
    setAssignments((prev) => prev.map((g) => ({ ...g, assigned })))
  }

  // Discard unsaved checkbox edits and go back to what the server last returned.
  const resetAssignments = () => {
    const saved = new Map(groups.map((g) => [g.locationId, g.assigned]))
    setAssignments((prev) => prev.map((g) => ({ ...g, assigned: saved.get(g.locationId) ?? false })))
    setTouched(false)
  }

  const handleSave = async () => {
    const groupIds = assignments.filter((g) => g.assigned).map((g) => g.locationId)
    await updateGroups.mutateAsync({ id: userId, groupIds })
    // Let the refetched server state drive the checkboxes again.
    setTouched(false)
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="text-xl font-semibold">
          Groups this User can access
          {assignments.length > 0 && (
            <span className="ml-2 text-sm font-normal text-gray-500">
              {assignedCount} of {assignments.length} selected
            </span>
          )}
        </h2>
        {assignments.length > 0 && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setAll(true)}
              disabled={allAssigned}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Select all
            </button>
            <button
              type="button"
              onClick={() => setAll(false)}
              disabled={assignedCount === 0}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Unselect all
            </button>
            <button
              type="button"
              onClick={resetAssignments}
              disabled={!hasChanges}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reset
            </button>
          </div>
        )}
      </div>

      {assignments.length > 0 ? (
        <>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">
                    <input
                      type="checkbox"
                      aria-label={allAssigned ? 'Unselect all groups' : 'Select all groups'}
                      checked={allAssigned}
                      ref={(el) => {
                        // Mixed selection shows the dash rather than a misleading tick.
                        if (el) el.indeterminate = assignedCount > 0 && !allAssigned
                      }}
                      onChange={() => setAll(!allAssigned)}
                      className="rounded border-gray-300 mr-2 align-middle"
                    />
                    Assigned
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Group ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Group Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase"># of Users</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase"># of RICs</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {assignments.map((group) => (
                  <tr
                    key={group.locationId}
                    className={group.assigned ? 'bg-blue-50 hover:bg-blue-100' : 'hover:bg-gray-50'}
                  >
                    <td className="px-4 py-3 whitespace-nowrap">
                      <input
                        type="checkbox"
                        aria-label={`Assign ${group.locationName}`}
                        checked={group.assigned}
                        onChange={() => toggle(group.locationId)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{group.locationId}</td>
                    <td className="px-4 py-3">{group.locationName}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{group.userCount}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{group.deviceCount}</td>
                    <td className="px-4 py-3 max-w-xs truncate" title={group.description || '-'}>
                      {group.description || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center gap-3 mt-6 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={handleSave}
              disabled={!hasChanges || updateGroups.isPending}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateGroups.isPending ? 'Saving...' : 'Save Groups'}
            </button>
            {hasChanges && <span className="text-sm text-amber-600">Unsaved changes</span>}
          </div>
        </>
      ) : (
        <p className="text-gray-600">Loading groups...</p>
      )}
    </div>
  )
}
